// ./src/pages/private/SandboxPage/SandboxPage.tsx
import React, { useState, useCallback, useEffect, useRef } from "react";
import { Stack, Grid, Group } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppToolbar,
  AppButton,
  EmptyState,
  LoadingDataBlock,
} from "@/components/generic";
import {
  FormSelector,
  SandboxCodePreview,
  SandboxFormPreview,
  FormThemeSelector,
} from "@/components/features";
import { useForms } from "@/hooks";
import { Form } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_THEME } from "@/config";

const SCRIPTS_ROOT_URL =
  import.meta.env.VITE_SCRIPTS_ROOT_URL || "https://cdn.formsubmits.com";
const API_ROOT_URL =
  import.meta.env.VITE_SUBMISSION_API_URL_BASE || "https://api.formsubmits.com";

export const SandboxPage: React.FC<PageProps> = (props: PageProps) => {
  const { isAuthenticated } = useApp();

  // Local UI state
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [selectedFormPath, setSelectedFormPath] = useState<string | null>(null);
  const [selectedFormName, setSelectedFormName] = useState<string | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<string>(DEFAULT_THEME);
  const [generatedCode, setGeneratedCode] = useState<string>("");
  const [editedCode, setEditedCode] = useState<string>("");
  const [isCodeEdited, setIsCodeEdited] = useState<boolean>(false);
  const [generatingCode, setGeneratingCode] = useState<boolean>(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false);
  const [scriptError, setScriptError] = useState<string | null>(null);

  // Track if script is already being loaded
  const scriptLoadingRef = useRef<boolean>(false);

  // Use forms hook to get form list only (for dropdown)
  const {
    forms,
    loading: formsLoading,
    isEmpty: noFormsExist,
  } = useForms({
    autoLoad: true,
    isAuthenticated,
  });

  // Load the FormSubmitsEmbed script
  useEffect(() => {
    // Check if already loaded
    if (window.FormSubmitsEmbed) {
      setScriptLoaded(true);
      return;
    }

    // Prevent duplicate loading
    if (scriptLoadingRef.current) {
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector(
      `script[src*="formsubmits-embed.js"]`
    );
    if (existingScript) {
      existingScript.addEventListener("load", () => {
        setScriptLoaded(true);
      });
      existingScript.addEventListener("error", () => {
        setScriptError("Failed to load form embed library");
      });
      return;
    }

    scriptLoadingRef.current = true;

    const script = document.createElement("script");
    script.src = `${SCRIPTS_ROOT_URL}/formsubmits-embed.js`;
    script.async = true;

    script.onload = () => {
      setScriptLoaded(true);
      scriptLoadingRef.current = false;
    };

    script.onerror = () => {
      setScriptError("Failed to load form embed library");
      scriptLoadingRef.current = false;
    };

    document.head.appendChild(script);
  }, []);

  // Auto-select first form when forms are loaded
  useEffect(() => {
    if (!formsLoading && forms.length > 0 && !selectedFormId) {
      const firstForm = forms[0];
      if (firstForm.id && firstForm.path) {
        setSelectedFormId(firstForm.id);
        setSelectedFormPath(firstForm.path);
        setSelectedFormName(firstForm.name || null);
      }
    }
  }, [formsLoading, forms, selectedFormId]);

  // Generate code using public API when form path, theme, or script loading changes
  useEffect(() => {
    if (!scriptLoaded || !window.FormSubmitsEmbed || !selectedFormPath) {
      return;
    }

    let cancelled = false;

    const generateCode = async () => {
      setGeneratingCode(true);
      setGenerationError(null);

      try {
        // Use the embed library's generateHtml which calls the public API
        const code = await window.FormSubmitsEmbed!.generateHtml(
          selectedFormPath,
          {
            theme: selectedTheme,
            submitLabel: "Submit",
            includeStyles: true,
            showDescription: true,
            apiUrl: API_ROOT_URL,
          }
        );

        if (!cancelled) {
          setGeneratedCode(code);
          setEditedCode(code);
          setIsCodeEdited(false);
        }
      } catch (error) {
        console.error("Failed to generate form HTML:", error);
        if (!cancelled) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to generate form";
          setGenerationError(errorMessage);
          setGeneratedCode("");
          setEditedCode("");
        }
      } finally {
        if (!cancelled) {
          setGeneratingCode(false);
        }
      }
    };

    generateCode();

    return () => {
      cancelled = true;
    };
  }, [selectedFormPath, selectedTheme, scriptLoaded]);

  // Handle form selection change
  const handleFormChange = useCallback((formId: string | null, form?: Form) => {
    setSelectedFormId(formId);
    setSelectedFormPath(form?.path || null);
    setSelectedFormName(form?.name || null);
    setGeneratedCode("");
    setEditedCode("");
    setIsCodeEdited(false);
    setGenerationError(null);
  }, []);

  // Handle theme change
  const handleThemeChange = useCallback((value: string | null) => {
    if (value) {
      setSelectedTheme(value);
      setIsCodeEdited(false);
    }
  }, []);

  // Handle code edit in the editor
  const handleCodeChange = useCallback(
    (newCode: string) => {
      setEditedCode(newCode);
      setIsCodeEdited(newCode !== generatedCode);
    },
    [generatedCode]
  );

  // Reset code to generated version
  const handleResetCode = useCallback(() => {
    setEditedCode(generatedCode);
    setIsCodeEdited(false);
  }, [generatedCode]);

  // The code to display in preview - use edited if changed, otherwise generated
  const previewCode = editedCode || generatedCode;

  // Determine current UI state
  const isLoadingForms = formsLoading && forms.length === 0;
  const isLoading = isLoadingForms || !scriptLoaded;

  return (
    <DashboardLayout {...props}>
      <Stack gap="md" style={{ height: "100%" }}>
        {/* Page toolbar */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "form-selector",
              items: [
                <FormSelector
                  key="form-selector"
                  value={selectedFormId}
                  onChange={handleFormChange}
                  placeholder="Select a form"
                  style={{ minWidth: 300 }}
                  required
                  disabled={isLoading}
                />,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "theme-selector",
              items: [
                <Group key="theme-group" gap="xs">
                  <FormThemeSelector
                    key="theme-selector"
                    value={selectedTheme}
                    onChange={handleThemeChange}
                    style={{ minWidth: 200 }}
                    disabled={isLoading || !selectedFormId}
                    required
                  />
                  {isCodeEdited && (
                    <AppButton
                      key="reset-btn"
                      variant="secondary"
                      size="sm"
                      iconName="RotateCcw"
                      onClick={handleResetCode}
                    >
                      Reset
                    </AppButton>
                  )}
                </Group>,
              ],
              align: "right",
            },
          ]}
        />

        {/* Script error state */}
        {scriptError && (
          <EmptyState
            display={true}
            title="Failed to load"
            description={scriptError}
            iconName="AlertTriangle"
          />
        )}

        {/* Loading forms or script state */}
        {!scriptError && isLoading && (
          <LoadingDataBlock
            text={
              !scriptLoaded ? "Loading embed library..." : "Loading forms..."
            }
          />
        )}

        {/* No forms exist state */}
        {!scriptError && !isLoading && noFormsExist && (
          <EmptyState
            display={true}
            title="No forms available"
            description="You need to create a form first to use the sandbox"
            iconName="FileText"
            actionLabel="Create Form"
            actionIcon="Plus"
            onAction={() => (window.location.href = "/forms")}
          />
        )}

        {/* Generation error state */}
        {!scriptError &&
          !isLoading &&
          !noFormsExist &&
          selectedFormPath &&
          generationError && (
            <EmptyState
              display={true}
              title="Failed to load form"
              description={generationError}
              iconName="AlertTriangle"
              actionLabel="Try Again"
              actionIcon="RefreshCw"
              onAction={() => {
                // Trigger re-generation by toggling theme
                const currentTheme = selectedTheme;
                setSelectedTheme("");
                setTimeout(() => setSelectedTheme(currentTheme), 0);
              }}
            />
          )}

        {/* Main content - code and preview */}
        {!scriptError &&
          !isLoading &&
          !noFormsExist &&
          selectedFormPath &&
          !generationError && (
            <Grid gutter="md" style={{ flex: 1, minHeight: 0 }}>
              <Grid.Col span={6} style={{ height: "calc(100vh - 220px)" }}>
                <SandboxCodePreview
                  code={previewCode}
                  loading={generatingCode}
                  error={null}
                  onChange={handleCodeChange}
                  editable={true}
                  downloadFilename={selectedFormName || "form"}
                  formName={selectedFormName || undefined}
                  theme={selectedTheme}
                />
              </Grid.Col>
              <Grid.Col span={6} style={{ height: "calc(100vh - 220px)" }}>
                <SandboxFormPreview
                  code={previewCode}
                  formName={selectedFormName || undefined}
                  loading={generatingCode}
                  error={null}
                  theme={selectedTheme}
                />
              </Grid.Col>
            </Grid>
          )}
      </Stack>
    </DashboardLayout>
  );
};
